import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { login as apiLogin, register as apiRegister, logout as apiLogout, getCurrentUser } from '@/lib/api';
import type { User } from '@shared/schema';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const prevTierRef = useRef<string | undefined>(undefined);

  // Fetch current user on mount
  const { isLoading, refetch, data } = useQuery<User | null>({
    queryKey: ['currentUser'],
    queryFn: getCurrentUser,
    retry: false,
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    staleTime: 30000, // Consider data stale after 30 seconds
  });

  // Refetch user when returning from checkout (on success or cancel)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('success') || params.has('canceled')) {
      refetch();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [refetch]);

  // Update user state when query data changes and invalidate premium tips on tier change
  useEffect(() => {
    if (data) {
      setUser(data);

      // If tier changed, invalidate premium tips
      if (prevTierRef.current !== undefined && prevTierRef.current !== data.subscriptionTier) {
        console.log('[Auth] Tier changed, invalidating premium tips');
        queryClient.invalidateQueries({ queryKey: ['premiumTips'] });
      }
      prevTierRef.current = data.subscriptionTier;
    } else {
      setUser(null);
    }
  }, [data, queryClient]);

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiLogin(email, password),
    onSuccess: (data) => {
      setUser(data);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      apiRegister(email, password),
    onSuccess: (data) => {
      setUser(data);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: apiLogout,
    onSuccess: () => {
      setUser(null);
      // Refetch to update auth state
      refetch();
    },
  });

  const stableRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login: async (email: string, password: string) => {
      const result = await loginMutation.mutateAsync({ email, password });
      return result;
    },
    register: async (email: string, password: string) => {
      const result = await registerMutation.mutateAsync({ email, password });
      return result;
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    refetch: stableRefetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
